import { useEffect, useRef, useState } from 'react'
import { ActionType, type Chart, type Crosshair } from 'klinecharts'
import type { EventBus } from '../core/EventBus'
import type { EarningsEvent } from '../types/earnings'
import { DEFAULT_SESSION_TZ } from '../utils/sessionDay'
import { beatMissColor } from '../utils/earningsMarkersIndicator'
import { CHART_FONT_FAMILY } from '../utils/chartColors'
import { matchEarnings } from './matchEarnings'

export interface EarningsTooltipProps {
  chart: Chart | null
  eventBus: EventBus | null
  /** Earnings events currently rendered as badges (host-supplied). */
  earnings: EarningsEvent[] | undefined
  /** Session timezone used to bucket a hovered bar into its trading day. */
  timezone: string
}

/** Compact `1.23` / `–` formatting for an optional numeric field. */
function fmtNum(n: number | null | undefined, digits = 2): string {
  return n != null && Number.isFinite(n) ? n.toFixed(digits) : '–'
}

/** Signed percent, e.g. `+4.1%` / `-2.0%` / `–`. */
function fmtPct(n: number | null | undefined): string {
  if (n == null || !Number.isFinite(n)) return '–'
  const sign = n > 0 ? '+' : ''
  return `${sign}${n.toFixed(1)}%`
}

/**
 * Hover card for an earnings marker. Canvas can't do rich hover, so — like
 * `CrosshairTimeLabel` — this thin React layer subscribes to the crosshair,
 * matches the hovered bar to an earnings event by session day, and renders a
 * compact card (date · period · EPS est→actual · surprise% · revenue% ·
 * BMO/AMC). Renders nothing when no earnings event is under the crosshair.
 */
export function EarningsTooltip({ chart, eventBus, earnings, timezone }: EarningsTooltipProps) {
  const wrapRef = useRef<HTMLDivElement>(null)
  const [hover, setHover] = useState<{ x: number; y: number; ev: EarningsEvent } | null>(null)

  useEffect(() => {
    if (!chart) return

    const tz = timezone || DEFAULT_SESSION_TZ
    const onCrosshair = (crosshair: Crosshair) => {
      const bar = crosshair.kLineData
      const x = crosshair.x ?? crosshair.realX
      const idx = crosshair.dataIndex ?? crosshair.realDataIndex
      if (!bar || x == null || idx == null) {
        setHover(null)
        return
      }
      // Needs the whole list, not just the hovered bar: the badge's bar is the
      // FIRST of its session day, which only the full series can identify.
      const ev = matchEarnings(earnings, chart.getDataList(), idx, tz)
      setHover(ev ? { x, y: crosshair.y ?? 0, ev } : null)
    }

    chart.subscribeAction(ActionType.OnCrosshairChange, onCrosshair)
    const unsubLeave = eventBus?.on('crosshair:leave', () => setHover(null))

    return () => {
      chart.unsubscribeAction(ActionType.OnCrosshairChange, onCrosshair)
      unsubLeave?.()
    }
  }, [chart, eventBus, earnings, timezone])

  // Clamp the card within the container width so it never overflows the right
  // edge. Width guess (~180px) keeps the clamp cheap without measuring.
  const width = wrapRef.current?.clientWidth ?? 0
  const CARD_W = 180
  let left = hover?.x ?? 0
  if (hover && width > 0) {
    left = Math.min(Math.max(left, 8), Math.max(8, width - CARD_W - 8))
  }

  const ev = hover?.ev

  return (
    <div
      ref={wrapRef}
      data-testid="earnings-tooltip-wrap"
      className="pointer-events-none absolute inset-0 z-20"
    >
      {ev && (
        <div
          data-testid="earnings-tooltip"
          className="absolute rounded-md border border-border bg-[hsl(var(--chart-canvas))] px-2.5 py-2 text-[11px] leading-tight text-[hsl(var(--chart-text))] shadow-xl shadow-black/40"
          style={{
            left,
            bottom: 28,
            width: CARD_W,
            fontFamily: CHART_FONT_FAMILY,
          }}
        >
          <div className="mb-1 flex items-center justify-between gap-2">
            <span className="inline-flex items-center gap-1.5 font-semibold">
              <span
                className="inline-block h-2 w-2 rounded-full"
                style={{ background: beatMissColor(ev) }}
              />
              {ev.date}
            </span>
            {(ev.period || ev.periodYear != null) && (
              <span className="text-[hsl(var(--chart-text-muted))]">
                {ev.period ?? ''}
                {ev.periodYear != null ? ` ${ev.periodYear}` : ''}
              </span>
            )}
          </div>
          <div className="flex justify-between">
            <span className="text-[hsl(var(--chart-text-muted))]">EPS est → act</span>
            <span>
              {fmtNum(ev.epsEstimate)} → {fmtNum(ev.epsActual)}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-[hsl(var(--chart-text-muted))]">EPS surprise</span>
            <span style={{ color: beatMissColor(ev) }}>{fmtPct(ev.epsSurprisePct)}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-[hsl(var(--chart-text-muted))]">Rev surprise</span>
            <span>{fmtPct(ev.revenueSurprisePct)}</span>
          </div>
          {ev.announceTime && (
            <div className="flex justify-between">
              <span className="text-[hsl(var(--chart-text-muted))]">Timing</span>
              <span>{ev.announceTime}</span>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
